import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DownloadSimple, PenNib, Eye, ArrowSquareOut, Printer } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const GROUP_ORDER = [
  "Intake Packet",
  "Consent",
  "Acknowledgements",
  "Client forms — designated caregiver & Medicaid",
  "Hiring",
  "Onboarding packet",
  "Tax & payroll",
  "Employee forms",
  "Office signage",
  "Drafts — pending review",
];

export default function DocumentLibraryPage() {
  const [docs, setDocs] = useState(null);

  useEffect(() => {
    api.get("/document-library")
      .then((r) => setDocs(r.data))
      .catch((e) => { toast.error(formatApiError(e)); setDocs([]); });
  }, []);

  // Same-origin cookie auth lets us point the browser straight at the API — no blob juggling,
  // and window.open stays inside the click handler so popup blockers don't interfere.
  const download = (d) => {
    const a = document.createElement("a");
    a.href = `${api.defaults.baseURL}/document-library/${d.key}/download`;
    a.download = d.key + ".pdf";
    a.click();
    toast.success("Download started");
  };

  const openFillable = (d) => {
    window.open(`${api.defaults.baseURL}/document-library/${d.key}/view?fillable=1`, "_blank");
  };

  const printPacket = () => {
    const a = document.createElement("a");
    a.href = `${api.defaults.baseURL}/templates/signing-packet.pdf`;
    a.download = "blank-signing-packet.pdf";
    a.click();
    toast.success("Download started");
  };

  const groups = docs
    ? [...GROUP_ORDER, ...[...new Set(docs.map((d) => d.group))].filter((g) => !GROUP_ORDER.includes(g))]
        .map((g) => [g, docs.filter((d) => d.group === g)])
        .filter(([, items]) => items.length)
    : [];

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6" data-testid="documents-page">
      <h1 className="font-heading text-3xl font-bold text-stone-900" data-testid="documents-title">Document library</h1>
      <p className="mt-1 text-sm text-stone-500">
        Every blank form in one place. <b>Fill online</b> opens the form's online companion — fill it in and sign on screen,
        and the completed PDF downloads automatically. <b>Download</b> saves the blank PDF for printing.
      </p>
      {docs === null ? (
        <div className="flex justify-center py-20"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>
      ) : (
        groups.map(([group, items]) => (
          <div key={group} className="mt-8" data-testid={`doc-group-${group.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-stone-400">{group}</p>
              {group === "Intake Packet" && (
                <button type="button" onClick={printPacket}
                  className="inline-flex h-8 items-center gap-1.5 rounded-full border border-stone-300 px-3 text-xs font-medium text-stone-600 transition-colors hover:bg-stone-900 hover:text-white"
                  data-testid="print-blank-signing-packet">
                  <Printer size={14} /> Print the whole blank packet
                </button>
              )}
            </div>
            <div className="divide-y divide-stone-100 rounded-2xl border border-stone-200 bg-white shadow-sm">
              {items.map((d) => (
                <div key={d.key} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3.5" data-testid={`doc-row-${d.key}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-stone-900">{d.title}</p>
                    {d.note && <p className="text-xs text-stone-400">{d.note}</p>}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {d.mode === "acro" && (
                      <button type="button" onClick={() => openFillable(d)}
                        className="inline-flex h-9 items-center gap-1.5 rounded-full border border-stone-300 px-3 text-sm font-medium text-stone-700 transition-colors hover:bg-stone-900 hover:text-white"
                        data-testid={`doc-fill-${d.key}`}>
                        <PenNib size={15} /> Fill online
                      </button>
                    )}
                    {(d.mode === "form" || d.mode === "sign") && (
                      <Link to={`/documents/${d.key}`}
                        className="inline-flex h-9 items-center gap-1.5 rounded-full bg-primary px-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-stone-900"
                        data-testid={`doc-fill-${d.key}`}>
                        <PenNib size={15} /> Fill online
                      </Link>
                    )}
                    {d.mode === "view" && (
                      <Link to={`/documents/${d.key}`}
                        className="inline-flex h-9 items-center gap-1.5 rounded-full border border-stone-300 px-3 text-sm font-medium text-stone-700 transition-colors hover:bg-stone-900 hover:text-white"
                        data-testid={`doc-view-${d.key}`}>
                        <Eye size={15} /> View online
                      </Link>
                    )}
                    {d.mode === "link" && (
                      <Link to={d.form_url}
                        className="inline-flex h-9 items-center gap-1.5 rounded-full bg-primary px-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-stone-900"
                        data-testid={`doc-fill-${d.key}`}>
                        <ArrowSquareOut size={15} /> Open online form
                      </Link>
                    )}
                    <button type="button" onClick={() => download(d)}
                      className="inline-flex h-9 items-center gap-1.5 rounded-full border border-stone-300 px-3 text-sm font-medium text-stone-700 transition-colors hover:bg-stone-900 hover:text-white"
                      data-testid={`doc-download-${d.key}`}>
                      <DownloadSimple size={15} /> Download
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
